import React, { useCallback, useEffect, useRef } from 'react';
import './MemoizedIframeRendererComponent.scss';

interface MemoizedIframeRendererProps {
    src: string;
    title?: string;
    className?: string;
    width?: string | number;
    height?: string | number;
    loading?: 'lazy' | 'eager';
    sandbox?: string;
    role?: string;
    onLoad?: (donotTriggerErrorCard?: boolean) => void;
    onError?: () => void;
    onPaused?: () => void;
    onLoadStart?: () => void;
    onRestart?: () => void;
    onTimeout?: () => void;
    allowFullScreen?: boolean;
    allow?: string;
    referrerPolicy?: React.HTMLAttributeReferrerPolicy;
    style?: React.CSSProperties;
    id?: string;
    'data-testid'?: string;
    autoScreenshot?: boolean;
    screenshotFileName?: string;
    onScreenshotComplete?: (imageDataUrl: string) => void;
    screenshotMethod?: 'screen-capture' | 'html2canvas';
    timeoutMs?: number; // Timeout in milliseconds (default: 10000ms = 10s)
}

const MemoizedIframeRenderer = (props: MemoizedIframeRendererProps) => {
    const { src, title = "App Preview", className = "border-none project-preview-iframe", width = "100%", height, loading = "eager", sandbox = "allow-scripts allow-same-origin allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-downloads allow-popups", role = "document", onLoad, onError, onPaused, onLoadStart, onRestart, allowFullScreen = false, referrerPolicy, style, id, 'data-testid': dataTestId } = props;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeLoadedRef = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Reset loading state when src changes
    useEffect(() => {
        iframeLoadedRef.current = false;


    }, [src]);

    // Fallback timeout: trigger onLoad after timeout if native onLoad doesn't fire
    useEffect(() => {
        // Only set up timeout if we have a src and it hasn't loaded yet
        if (!src || iframeLoadedRef.current) return;

        const timeoutDuration = props.timeoutMs || 30000;

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Set up a fallback timeout
        timeoutRef.current = setTimeout(() => {
            if (!iframeLoadedRef.current) {
                console.log(`Iframe onLoad timeout reached (${timeoutDuration}ms), triggering fallback onLoad for:`, src);
                iframeLoadedRef.current = true;
                onLoad?.(true);
            }
        }, timeoutDuration);

        // Cleanup timeout on unmount or src change
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [src, onLoad, props.timeoutMs]);

    // Listen for postMessage events from iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Verify the message is from the iframe (optional security check)
            // Note: event.origin can be used for additional security if needed
            if (event.data && typeof event.data === 'object' && event.data.event === 'restart') {
                onRestart?.();
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [onRestart]);

    const checkIframeUrlForErrors = useCallback(async () => {
        if (!src) {
            return;
        }

        try {
            const response = await fetch(src, {
                method: 'GET',
                credentials: 'omit',
                mode: 'cors',
            });


            console.log('sjkdnfjkdfkjdsn', response);

            // Only handle actual HTTP errors (like 400 for paused apps)
            if (!response.ok && (response.status > 300 && response.status < 500) || response.status === 504) {

                console.log('sjkdnfjkdfkjdsn', response.status);
                // Status 400 means app is paused - call onPaused handler
                onPaused?.();
                return;
            }

            // If response is ok, everything is fine
        } catch (error) {

            console.log('sjkdnfjkdfkjdsn', error);

            // Fetch failed - could be CORS error or network error
            // If the iframe loaded successfully, CORS errors are expected and not real problems
            // The iframe can load cross-origin content even if fetch can't due to CORS
            const isCorsError = error instanceof TypeError &&
                (
                    error.message.includes('CORS') ||
                    error.message.includes('network'));

            // const fetchError = error instanceof Error && error.message.includes('Failed to fetch');

            // if(fetchError) {
            //     onPaused?.();
            //     return;
            // }

            if (isCorsError && iframeLoadedRef.current) {
                // If iframe already loaded, CORS error in fetch is not a real problem
                // The URL is valid and working - we just can't read it via fetch due to CORS
                console.log('CORS error when checking URL, but iframe loaded successfully - URL is valid:', src);

                return;
            }

            // If iframe didn't load and we got a fetch error, it might be a real network error
            // But we can't be sure, so we'll let the iframe's onError handle it
            // Only log for debugging
            if (isCorsError) {
                onPaused?.();

                console.log('CORS error when checking URL (iframe may still load):', src);
            } else {
                console.error('Error checking iframe URL:', error);
            }
            // Don't call onError here - let the iframe's onError handle actual loading failures
        }
    }, [src, onPaused]);

    // Memoized load handler
    const handleLoad = useCallback(() => {
        // Mark iframe as loaded successfully
        iframeLoadedRef.current = true;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        onLoad?.();
        checkIframeUrlForErrors();
    }, [onLoad, checkIframeUrlForErrors]);

    // Memoized error handler
    const handleError = useCallback(() => {
        iframeLoadedRef.current = false;
        onError?.();
    }, [onError]);

    const handleLoadStart = useCallback(() => {
        onLoadStart?.();
    }, [onLoadStart]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe) {
            const nativeLoad = () => handleLoad();
            iframe.addEventListener('load', nativeLoad);
            return () => iframe.removeEventListener('load', nativeLoad);
        }
    }, [handleLoad]);



    return (
        <iframe
            {...props}
            ref={iframeRef}
            title={title}
            className={className}
            width={width}
            height={height}
            loading={loading}
            sandbox={sandbox}
            role={role}
            onLoad={handleLoad}
            onError={handleError}
            onLoadStart={handleLoadStart}
            allowFullScreen={allowFullScreen}
            referrerPolicy={referrerPolicy}
            style={style}
            id={id}
            data-hj-allow-iframe
            data-testid={dataTestId}
            allow="accelerometer; autoplay; camera; encrypted-media; fullscreen; geolocation; gyroscope; microphone; midi; clipboard-read; clipboard-write; payment; usb; vr; xr-spatial-tracking; screen-wake-lock; magnetometer; ambient-light-sensor; battery; gamepad; picture-in-picture; display-capture; bluetooth;"
        />
    );
};


export default React.memo(MemoizedIframeRenderer);

